import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {firstValueFrom} from 'rxjs';
import {HrTool} from '@frontend/directus';
import {
  EvaluationCriteriaWithScores,
  ParticipatingToolResponse,
  ProjectChapterType,
  ProjectCoverageDataControllerService,
  ProjectCoverageDataResponse,
  ProjectStepType,
  ProviderScoresControllerService,
  TenderPhaseDetailsResponse,
  TenderPhaseType,
  TenderSelection,
} from '@frontend/shared-api';
import {InvitationTableComponent} from '../../../../../../shared/components/table/invitation-table/invitation-table.component';
import {
  InvitationTableTemplateContext,
  InviteToolsResponseOf,
  PhaseInvitationConfig,
  ProviderDecisionReason,
} from '../../../../../../shared/components/table/invitation-table/invitation-table.types';
import {RfpParticipatingTool, SuitabilityMode, SuitabilityThresholds} from './rfp-invitation.types';
import {dataCompleteness, isPreferred} from './rfp-suitability.calc';
import {
  countryEmployeeCoverage,
  functionCoverage,
  weightedCoverage,
} from '../../chapter-5-rfi/step-5-3-rfi-invitation-list/rfi-weighted-coverage.calc';
import {RfpSuitabilityPanelComponent} from './rfp-suitability-panel/rfp-suitability-panel.component';
import {RfpSelectionChange} from './rfp-suitability-panel/rfp-suitability-panel.types';
import {RfpCoverageCellComponent} from './rfp-coverage-cell/rfp-coverage-cell.component';
import {AppNumberPipe} from '@frontend/shared';
import {InvitationDataLoaderService} from '../../../../../../shared/services/invitation-data-loader.service';
import {
  ProviderTotalScoreCalculationService,
  ProviderTotalScoreResult,
} from '../../../../../../shared/services/provider-total-score-calculation.service';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {ResultScoreCellComponent} from '../../../../../../shared/components/result-score-cell/result-score-cell.component';
import {DecisionReasonModalComponent} from '../../../../../../shared/components/decision-reason-modal/decision-reason-modal.component';

@Component({
  selector: 'app-step-8-3-rfp-invitation-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    InvitationTableComponent,
    Button,
    Tooltip,
    ResultScoreCellComponent,
    DecisionReasonModalComponent,
    RfpSuitabilityPanelComponent,
    RfpCoverageCellComponent,
    AppNumberPipe,
  ],
  templateUrl: './step-6-3-rfp-invitation-list.component.html',
})
export class Step63RfpInvitationListComponent
  extends ProjectBaseStepComponent
  implements AfterViewInit
{
  private readonly transloco = inject(TranslocoService);
  private readonly invitationDataLoader = inject(InvitationDataLoaderService);
  private readonly providerTotalScoreCalculationService = inject(
    ProviderTotalScoreCalculationService,
  );
  private readonly coverageDataController = inject(ProjectCoverageDataControllerService);
  private readonly providerScoresController = inject(ProviderScoresControllerService);

  protected readonly suitabilityMode = signal<SuitabilityMode>('combined');
  protected readonly thresholds = signal<SuitabilityThresholds>({coverage: 70, rfi: 65});
  protected readonly selectedToolId = signal<string | null>(null);
  private readonly tableReady = signal(false);
  protected readonly matrixTools = computed(() =>
    this.tableReady()
      ? ((this.invitationTable()?.toolItems() ?? []) as RfpParticipatingTool[])
      : [],
  );

  readonly coverageTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfpParticipatingTool>>>(
      'coverageTemplate',
    );
  readonly dataCompletenessTemplate = viewChild<
    TemplateRef<InvitationTableTemplateContext<RfpParticipatingTool>>
  >('dataCompletenessTemplate');
  readonly providerTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfpParticipatingTool>>>(
      'providerTemplate',
    );
  private readonly coverageTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfpParticipatingTool>
  > | null>(null);
  private readonly dataCompletenessTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfpParticipatingTool>
  > | null>(null);
  private readonly providerTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfpParticipatingTool>
  > | null>(null);

  readonly totalResultTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfpParticipatingTool>>>(
      'totalResultTemplate',
    );

  protected readonly currentPhase = signal<TenderPhaseDetailsResponse | null>(null);
  private readonly rfiProviderScores = signal<Map<string, ProviderTotalScoreResult>>(new Map());
  protected readonly rfiWeightSum = signal(0);
  private readonly totalResultTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfpParticipatingTool>
  > | null>(null);

  private readonly RFI_COMPLETION_STEP: {chapterId: ProjectChapterType; stepId: ProjectStepType} = {
    chapterId: ProjectChapterType.RFI,
    stepId: ProjectStepType.RFI_PROVIDER_RESULTS,
  };

  protected readonly reasonModalVisible = signal(false);
  protected readonly selectedReasonTool = signal<RfpParticipatingTool | null>(null);

  protected openReasonModal(item: RfpParticipatingTool): void {
    this.selectedReasonTool.set(item);
    this.reasonModalVisible.set(true);
  }

  protected closeReasonModal(): void {
    this.reasonModalVisible.set(false);
    this.selectedReasonTool.set(null);
  }

  readonly invitationTable = viewChild(InvitationTableComponent);

  protected onReasonSaved(updatedReason: unknown): void {
    const reason = updatedReason as {
      participatingToolId?: number;
      mainReasonId?: string | null;
      detailReasonIds?: string[];
      generatedText?: string;
      comment?: string;
    };

    const tool = this.selectedReasonTool();
    if (tool && reason.mainReasonId) {
      const updatedDecisionReason: ProviderDecisionReason = {
        mainReasonId: reason.mainReasonId,
        detailReasonIds: reason.detailReasonIds ?? [],
        generatedText: reason.generatedText ?? '',
        comment: reason.comment ?? null,
        hasValue: true,
      };
      this.invitationTable()?.updateToolItemById(tool.id, {
        decisionReason: updatedDecisionReason,
      } as Partial<RfpParticipatingTool>);
    }

    this.closeReasonModal();
  }

  protected readonly rfpPhaseConfig = computed((): PhaseInvitationConfig<RfpParticipatingTool> => ({
    phaseType: TenderPhaseType.RFP,
    timeFrameNumber: 3,
    tableConfig: {
      columns: [
        {
          field: 'toolProvider.name',
          header: 'project.steps.invitationList.provider',
          sortable: true,
          width: '16%',
          template: this.providerTemplateSignal() ?? undefined,
        },
        {
          field: 'translatedName',
          header: 'project.steps.invitationList.tool',
          sortable: true,
          width: '14%',
        },
        {
          field: 'suitability.combinedCoverage',
          header: 'project.steps.rfp.invitationList.matrix.coverageColumn',
          sortable: true,
          width: '16%',
          template: this.coverageTemplateSignal() ?? undefined,
        },
        {
          field: 'totalResult',
          header: 'project.steps.invitationList.totalResult',
          width: '14%',
          template: this.totalResultTemplateSignal() ?? undefined,
        },
        {
          field: 'suitability.dataCompleteness',
          header: 'project.steps.rfp.invitationList.matrix.dataCompleteness',
          sortable: true,
          width: '10%',
          template: this.dataCompletenessTemplateSignal() ?? undefined,
        },
        {
          field: 'selection',
          header: 'project.steps.invitationList.selection',
          sortable: true,
          sortField: 'selection',
          width: '11%',
        },
        {
          field: 'decisionReason',
          header: 'project.steps.invitationList.reason',
          width: '11%',
          template: this.reasonTemplateSignal() ?? undefined,
        },
        {
          field: 'status',
          header: 'project.steps.invitationList.status',
          sortable: true,
          sortField: 'status',
          width: '8%',
        },
      ],
      quickFilter: {
        options: [
          {value: 'all', labelKey: 'project.steps.rfp.invitationList.matrix.filterAll'},
          {value: 'preferred', labelKey: 'project.steps.rfp.invitationList.matrix.filterPreferred'},
          {
            value: 'participants',
            labelKey: 'project.steps.rfp.invitationList.matrix.filterParticipants',
          },
        ],
        matches: (item: RfpParticipatingTool, value: string) => this.matchesFilter(item, value),
        searchText: (item: RfpParticipatingTool) =>
          `${item.toolProvider.name} ${item.translatedName ?? item.name}`,
        searchPlaceholderKey: 'project.steps.rfi.invitationList.searchPlaceholder',
        hintKey: 'project.steps.rfp.invitationList.matrix.tableHint',
      },
      emptyMessage: 'project.steps.rfp.invitationList.noData',
    },
    timeframeConfig: {
      tooltipKeys: {
        plannedShippingDate: 'project.steps.rfp.invitationList.plannedShippingDate',
        plannedSubmissionDate: 'project.steps.rfp.invitationList.plannedSubmissionDate',
      },
    },
    prerequisiteChecker: () => this.prerequisitesMet(),
    stepLockedFn: () => this.isCurrentStepLocked(),
    dataLoader: (projectId) => this.loadRfpData(projectId),
    translationKeys: {
      title: 'project.steps.rfp.invitationList.title',
    },
  }));

  readonly reasonTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfpParticipatingTool>>>('reasonTemplate');
  private readonly reasonTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfpParticipatingTool>
  > | null>(null);

  ngAfterViewInit(): void {
    this.tableReady.set(true);
    const totalResultTemplate = this.totalResultTemplate();
    if (totalResultTemplate) {
      this.totalResultTemplateSignal.set(totalResultTemplate);
    }
    const reasonTemplate = this.reasonTemplate();
    if (reasonTemplate) {
      this.reasonTemplateSignal.set(reasonTemplate);
    }
    this.coverageTemplateSignal.set(this.coverageTemplate() ?? null);
    this.dataCompletenessTemplateSignal.set(this.dataCompletenessTemplate() ?? null);
    this.providerTemplateSignal.set(this.providerTemplate() ?? null);
  }

  protected selectTool(toolId: string): void {
    this.selectedToolId.set(toolId);
  }

  protected async setSelection(change: RfpSelectionChange): Promise<void> {
    await this.invitationTable()?.changeSelection(change.toolId, change.selection);
  }

  private matchesFilter(tool: RfpParticipatingTool, value: string): boolean {
    if (value === 'preferred') {
      return isPreferred(tool, this.suitabilityMode(), this.thresholds());
    }
    if (value === 'participants') {
      return tool.selection === TenderSelection.PARTICIPANT;
    }
    return true;
  }

  onCurrentPhaseChange(phase: TenderPhaseDetailsResponse | null): void {
    this.currentPhase.set(phase);
  }

  private readonly prerequisitesMet = computed(() => {
    this.stepState.chaptersWithStatuses();

    const chaptersCompleted = [
      ProjectChapterType.PROJECT_SETUP,
      ProjectChapterType.PRINCIPLE_DEFINITION,
      ProjectChapterType.ACTUAL_SYSTEMS,
      ProjectChapterType.EVALUATION_CRITERIA,
      ProjectChapterType.RFI,
    ].every((chapterId) => this.stepState.isChapterCompleted(chapterId));

    const specificStepsCompleted = this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFP, stepId: ProjectStepType.RFP_EVALUATION_SUBCRITERIA},
      {chapterId: ProjectChapterType.RFP, stepId: ProjectStepType.RFP_INVITATION_PREVIEW},
    ]);

    return chaptersCompleted && specificStepsCompleted;
  });

  private async loadRfpData(
    projectId: number,
  ): Promise<InviteToolsResponseOf<RfpParticipatingTool>> {
    const [
      {directusHrTools, participatingToolsResponse},
      rfiProviderTotals,
      coverageData,
      rfiProviderScores,
    ] = await Promise.all([
      firstValueFrom(
        this.invitationDataLoader.loadParticipatingToolsData(projectId, TenderPhaseType.RFP),
      ),
      this.providerTotalScoreCalculationService.calculateProviderTotalsWithWeight(
        projectId,
        TenderPhaseType.RFI,
      ),
      firstValueFrom(this.coverageDataController.getDataForCalculation({projectId})),
      firstValueFrom(
        this.providerScoresController.getProviderScoresByPhase({
          projectId,
          phase: TenderPhaseType.RFI,
        }),
      ),
    ]);

    this.rfiProviderScores.set(rfiProviderTotals.totals);
    this.rfiWeightSum.set(rfiProviderTotals.totalWeight);

    const toolItems = participatingToolsResponse.tools
      .map((rfpTool: ParticipatingToolResponse) => {
        const hrTool = directusHrTools.find((tool: HrTool) => tool.id === rfpTool.hrToolDirectusId);
        return hrTool
          ? this.mapToParticipatingTool(
              hrTool,
              rfpTool,
              coverageData,
              rfiProviderScores.evaluationCriteria,
            )
          : null;
      })
      .filter((tool: RfpParticipatingTool | null): tool is RfpParticipatingTool => tool !== null);

    return {
      tools: toolItems,
      currentPhase: participatingToolsResponse.currentPhase,
      maxAllowedParticipants: participatingToolsResponse.maxAllowedParticipants,
      calculatedPhaseSubmissionDate: participatingToolsResponse.calculatedPhaseSubmissionDate,
    };
  }

  private mapToParticipatingTool(
    tool: HrTool,
    rfpTool: ParticipatingToolResponse,
    coverageData: ProjectCoverageDataResponse,
    rfiCriteria: EvaluationCriteriaWithScores[],
  ): RfpParticipatingTool {
    const lang = this.transloco.getActiveLang();
    const translation = tool.translations.find((t) => t.language === lang);
    const rfiScore = this.rfiProviderScores().get(tool.id.toString());

    const hasDecisionReason = !!rfpTool.rfpMainReasonUuid;
    const availableFunctionIds = tool.moduleFunctions.map((moduleFunction) => moduleFunction.id);
    const availableCountryIsoCodes = tool.availableCountries.map((country) => country.isoCode);

    return {
      ...tool,
      translatedName: (translation?.name?.trim() !== '' ? translation?.name : tool.name) ?? null,
      participatingToolId: rfpTool.id,
      selection: rfpTool.rfpSelection ?? null,
      status: rfpTool.rfpStatus,
      rfiPercentage: rfiScore?.percentage ?? null,
      suitability: {
        combinedCoverage: weightedCoverage(
          coverageData.requirements,
          availableFunctionIds,
          availableCountryIsoCodes,
        ).score,
        functionCoverage: functionCoverage(coverageData.requirements, availableFunctionIds),
        countryCoverage: countryEmployeeCoverage(
          coverageData.countryEmployees,
          availableCountryIsoCodes,
        ),
        dataCompleteness: dataCompleteness(rfiCriteria, tool.id),
      },
      decisionReason: hasDecisionReason
        ? {
            mainReasonId: rfpTool.rfpMainReasonUuid ?? null,
            detailReasonIds: rfpTool.rfpDetailReasonUuids ?? [],
            generatedText: rfpTool.rfpReasonText ?? '',
            comment: rfpTool.rfpReasonComment ?? null,
            hasValue: true,
          }
        : null,
    };
  }

  navigateToRfi() {
    this.stepState.setActiveStep(
      this.RFI_COMPLETION_STEP.chapterId,
      this.RFI_COMPLETION_STEP.stepId,
    );
  }
}
